import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogoutButton } from '@/features/auth/components/LogoutButton';
import { useAuth } from '@/hooks/useAuth';
import {
  Bell,
  User,
  Settings,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { APP_CONFIG } from '@/config/constants';
import { ROUTES } from '@/utils/constants';

interface HeaderProps {
  onMenuToggle?: () => void;
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({
  className
}) => {
  const { user } = useAuth();
  const profile = user as { name?: string; email?: string; preferred_username?: string; "cognito:username"?: string } | null;
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [notificationCount] = React.useState(0); // Mock notification count
  
  // Check if email is valid (not a default/placeholder value)
  const isValidEmail = profile?.email && profile.email.includes("@");
  const displayName = profile?.name || 
                     (isValidEmail ? profile.email : null) || 
                     profile?.preferred_username || 
                     profile?.["cognito:username"];

  return (
    <header className={cn(
      'sticky top-0 z-50 w-full border-b border-gray-200 bg-white/90 backdrop-blur-2xl',
      className
    )}>
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          {/* Logo and title */}
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">A</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold">{APP_CONFIG.NAME}</h1>
            </div>
          </Link>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
                <span className="sr-only">Notifications</span>
              </Button>

              {/* User menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-3"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                  <span className="hidden sm:inline text-sm">{displayName}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>

                {/* User dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-md border bg-popover p-1 shadow-md">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium">{displayName}</p>
                    </div>

                    <div className="py-1">
                      <Link
                        to="/my-account"
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4" />
                        My Account
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </Link>
                    </div>

                    <div className="border-t pt-1">
                      <LogoutButton
                        variant="ghost"
                        className="w-full justify-start px-3 py-2 h-auto"
                        onLogout={() => setIsUserMenuOpen(false)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link to={ROUTES.LOGIN}>
              <Button variant="default" size="default">
                Login
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
